/**
 * Volume LIC (Line Integral Convolution) Renderer
 *
 * Implements GPU-based volume rendering for 3D LIC visualization.
 * Uses ray marching in fragment shaders to render the LIC volume
 * with transfer function-based coloring.
 */

class VolumeLICRenderer {
    constructor(scene, colormap = 'jet') {
        this.scene = scene;
        this.colormap = colormap;
        this.mesh = null;
        this.licTexture = null;
        this.magnitudeTexture = null;
        this.velocityTexture = null;
        this.colormapTexture = null;
        this.material = null;

        // Volume parameters
        this.opacity = 0.5;
        this.threshold = 0.1;
        this.steps = 128;
        this.brightness = 1.0;
        this.showMagnitude = true;
        this.licWeight = 0.5; // Balance between LIC pattern and magnitude coloring

        // Animation parameters
        this.animated = true;
        this.animationSpeed = 1.0;
        this.time = 0;
        this.animationId = null;
        this.lastFrameTime = 0;

        // Bounding box
        this.bounds = null;
        this.dimensions = null;

        console.log('[VolumeLIC] Renderer initialized');
    }

    /**
     * Create Volume LIC visualization from server data
     * @param {Object} data - Data from VolumeLICFilter
     * @param {Object} options - Visualization options
     * @returns {THREE.Mesh} - The created mesh
     */
    createVolume(data, options = {}) {
        console.log('[VolumeLIC] Creating volume visualization...');

        // Parse options
        this.opacity = options.opacity ?? 0.5;
        this.threshold = options.threshold ?? 0.1;
        this.steps = options.steps ?? 128;
        this.brightness = options.brightness ?? 1.0;
        this.licWeight = options.licWeight ?? 0.5;

        // Store volume info
        this.bounds = data.bounds;
        this.dimensions = data.dimensions;
        const [nx, ny, nz] = this.dimensions;

        // Preserve animation state before dispose (dispose sets animated=false)
        const wasAnimated = this.animated;
        const savedAnimationSpeed = this.animationSpeed;

        // Remove existing mesh
        this.dispose();

        // Restore animation state after dispose
        this.animated = wasAnimated;
        this.animationSpeed = savedAnimationSpeed;

        // Decode LIC volume data
        console.log('[VolumeLIC] Decoding volume data...');
        const licData = this._decodeBase64Float32(data.lic_data, nx * ny * nz);
        const magData = this._decodeBase64Float32(data.magnitude_data, nx * ny * nz);

        // Create 3D textures
        console.log('[VolumeLIC] Creating 3D textures...');
        this.licTexture = this._create3DTexture(licData, nx, ny, nz);
        this.magnitudeTexture = this._create3DTexture(magData, nx, ny, nz);

        // Decode velocity data for animation (if available)
        if (data.velocity_data) {
            console.log('[VolumeLIC] Decoding velocity data for animation...');
            const velData = this._decodeBase64Float32(data.velocity_data, nx * ny * nz * 3);
            this.velocityTexture = this._create3DTextureRGB(velData, nx, ny, nz);
        }

        // Create colormap texture
        this._createColormapTexture();

        // Create volume geometry (a cube that will be ray-marched)
        const [xMin, xMax, yMin, yMax, zMin, zMax] = this.bounds;
        const width = xMax - xMin;
        const height = yMax - yMin;
        const depth = zMax - zMin;

        const geometry = new THREE.BoxGeometry(width, height, depth);

        // Create shader material
        this.material = this._createShaderMaterial(data);

        // Create mesh
        this.mesh = new THREE.Mesh(geometry, this.material);
        this.mesh.position.set(
            (xMin + xMax) / 2,
            (yMin + yMax) / 2,
            (zMin + zMax) / 2
        );
        this.mesh.name = 'VolumeLIC';
        this.mesh.userData.type = 'volume_lic';
        this.mesh.userData.magnitudeRange = data.magnitude_range;

        this.scene.add(this.mesh);

        console.log(`[VolumeLIC] Created volume ${nx}x${ny}x${nz}, bounds: [${xMin.toFixed(2)}, ${xMax.toFixed(2)}] x [${yMin.toFixed(2)}, ${yMax.toFixed(2)}] x [${zMin.toFixed(2)}, ${zMax.toFixed(2)}]`);

        // Auto-start animation if velocity data is available
        if (this.velocityTexture && this.animated) {
            this.startAnimation();
            console.log('[VolumeLIC] Animation enabled with velocity field');
        }

        return this.mesh;
    }

    /**
     * Decode base64 float32 data
     * @private
     */
    _decodeBase64Float32(base64, expectedLength) {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        const floats = new Float32Array(bytes.buffer);

        if (floats.length !== expectedLength) {
            console.warn(`[VolumeLIC] Expected ${expectedLength} floats, got ${floats.length}`);
        }

        return floats;
    }

    /**
     * Create a 3D texture from float data
     * @private
     */
    _create3DTexture(data, nx, ny, nz) {
        // Convert to Uint8 for texture (WebGL 1 compatibility)
        const uint8Data = new Uint8Array(nx * ny * nz);
        for (let i = 0; i < data.length; i++) {
            uint8Data[i] = Math.floor(Math.max(0, Math.min(1, data[i])) * 255);
        }

        // THREE.DataTexture3D is the correct name in Three.js r128
        // (newer versions use Data3DTexture)
        const TextureClass = THREE.DataTexture3D || THREE.Data3DTexture;

        if (!TextureClass) {
            throw new Error('3D texture support not available in this Three.js version');
        }

        const texture = new TextureClass(uint8Data, nx, ny, nz);
        texture.format = THREE.RedFormat;
        texture.type = THREE.UnsignedByteType;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.wrapR = THREE.ClampToEdgeWrapping;
        texture.needsUpdate = true;

        return texture;
    }

    /**
     * Create a 3D RGB texture from float data (for velocity vectors)
     * @private
     */
    _create3DTextureRGB(data, nx, ny, nz) {
        // Convert float32 RGB to Uint8 RGB
        const uint8Data = new Uint8Array(nx * ny * nz * 3);
        for (let i = 0; i < nx * ny * nz * 3; i++) {
            uint8Data[i] = Math.floor(Math.max(0, Math.min(1, data[i])) * 255);
        }

        const TextureClass = THREE.DataTexture3D || THREE.Data3DTexture;
        if (!TextureClass) {
            throw new Error('3D texture support not available in this Three.js version');
        }

        const texture = new TextureClass(uint8Data, nx, ny, nz);
        texture.format = THREE.RGBFormat;
        texture.type = THREE.UnsignedByteType;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.wrapR = THREE.ClampToEdgeWrapping;
        texture.needsUpdate = true;

        return texture;
    }

    /**
     * Parse hex color to RGB (0-1 range)
     * @private
     */
    _hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16) / 255,
            g: parseInt(result[2], 16) / 255,
            b: parseInt(result[3], 16) / 255
        } : { r: 0, g: 0, b: 0 };
    }

    /**
     * Interpolate between color stops
     * @private
     */
    _interpolateColors(colors, t) {
        if (colors.length === 0) return { r: 0, g: 0, b: 0 };
        if (colors.length === 1) return this._hexToRgb(colors[0]);

        t = Math.max(0, Math.min(1, t));

        const numStops = colors.length;
        const scaledT = t * (numStops - 1);
        const lowerIndex = Math.floor(scaledT);
        const upperIndex = Math.min(lowerIndex + 1, numStops - 1);
        const localT = scaledT - lowerIndex;

        const c1 = this._hexToRgb(colors[lowerIndex]);
        const c2 = this._hexToRgb(colors[upperIndex]);

        return {
            r: c1.r + (c2.r - c1.r) * localT,
            g: c1.g + (c2.g - c1.g) * localT,
            b: c1.b + (c2.b - c1.b) * localT
        };
    }

    /**
     * Sample colormap at position t (0-1)
     * @private
     */
    _sampleColormap(t) {
        // Use centralized Colormap if available
        if (window.Colormap && window.Colormap.getColor) {
            // Get solid color for volume LIC dropdown if using solid colormap
            const solidColorHex = window.Colormap.isSolidColor(this.colormap)
                ? window.Colormap.getSolidColor('volumeLICColormap')
                : null;
            const rgb = window.Colormap.getColor(t, this.colormap, solidColorHex);
            return {
                r: rgb[0] / 255,
                g: rgb[1] / 255,
                b: rgb[2] / 255
            };
        }

        // Fallback to local colormaps if Colormap not available
        const colormaps = {
            jet: ['#00007f', '#0000ff', '#00ffff', '#ffff00', '#ff0000', '#7f0000'],
            viridis: ['#440154', '#482878', '#3e4a89', '#31688e', '#26838f', '#1f9e89', '#35b779', '#6ece58', '#b5de2b', '#fde724'],
            coolwarm: ['#3b4cc0', '#6688ee', '#88aaff', '#aaccff', '#dddddd', '#ffccaa', '#ff9966', '#ee6644', '#b40426'],
            plasma: ['#0d0887', '#5c01a6', '#9c179e', '#cc4778', '#ed7953', '#fdb42f', '#f0f921'],
            rainbow: ['#ff0000', '#ff7f00', '#ffff00', '#00ff00', '#0000ff', '#4b0082', '#9400d3'],
            turbo: ['#30123b', '#4454c4', '#4777ef', '#1ac9c6', '#56e978', '#a0da39', '#e1c034', '#fca50a', '#e45503', '#d02c2c'],
            grayscale: ['#000000', '#ffffff']
        };

        const colors = colormaps[this.colormap] || colormaps.jet;
        return this._interpolateColors(colors, t);
    }

    /**
     * Create colormap texture
     * @private
     */
    _createColormapTexture() {
        if (this.colormapTexture) {
            this.colormapTexture.dispose();
        }

        const size = 256;
        const data = new Uint8Array(size * 4);

        for (let i = 0; i < size; i++) {
            const t = i / (size - 1);
            const color = this._sampleColormap(t);
            data[i * 4] = Math.floor(color.r * 255);
            data[i * 4 + 1] = Math.floor(color.g * 255);
            data[i * 4 + 2] = Math.floor(color.b * 255);
            data[i * 4 + 3] = 255;
        }

        this.colormapTexture = new THREE.DataTexture(data, size, 1, THREE.RGBAFormat);
        this.colormapTexture.needsUpdate = true;
        this.colormapTexture.minFilter = THREE.LinearFilter;
        this.colormapTexture.magFilter = THREE.LinearFilter;
    }

    /**
     * Create shader material for volume rendering with animation support
     * @private
     */
    _createShaderMaterial(data) {
        const [xMin, xMax, yMin, yMax, zMin, zMax] = this.bounds;
        const hasVelocity = this.velocityTexture !== null;

        const vertexShader = `
            varying vec3 vPosition;
            varying vec3 vWorldPosition;

            void main() {
                vPosition = position;
                vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `;

        // Fragment shader with animation support
        const fragmentShader = `
            precision highp float;
            precision highp sampler3D;

            uniform sampler3D licTexture;
            uniform sampler3D magnitudeTexture;
            ${hasVelocity ? 'uniform sampler3D velocityTexture;' : ''}
            uniform sampler2D colormapTexture;
            uniform vec3 volumeMin;
            uniform vec3 volumeMax;
            uniform float opacity;
            uniform float threshold;
            uniform int numSteps;
            uniform float brightness;
            uniform float licWeight;
            uniform float uTime;
            uniform float animationSpeed;

            varying vec3 vPosition;
            varying vec3 vWorldPosition;

            // Ray-box intersection
            vec2 intersectBox(vec3 origin, vec3 dir, vec3 boxMin, vec3 boxMax) {
                vec3 tMin = (boxMin - origin) / dir;
                vec3 tMax = (boxMax - origin) / dir;
                vec3 t1 = min(tMin, tMax);
                vec3 t2 = max(tMin, tMax);
                float tNear = max(max(t1.x, t1.y), t1.z);
                float tFar = min(min(t2.x, t2.y), t2.z);
                return vec2(tNear, tFar);
            }

            void main() {
                // Ray direction from camera through this fragment
                vec3 rayDir = normalize(vWorldPosition - cameraPosition);
                vec3 rayOrigin = cameraPosition;

                // Find intersection with volume bounds
                vec2 tHit = intersectBox(rayOrigin, rayDir, volumeMin, volumeMax);

                if (tHit.x > tHit.y || tHit.y < 0.0) {
                    discard;
                }

                // Start and end of ray within volume
                float tStart = max(tHit.x, 0.0);
                float tEnd = tHit.y;

                // Step size
                float stepSize = (tEnd - tStart) / float(numSteps);

                // Accumulated color and opacity
                vec4 accum = vec4(0.0);

                // Ray marching
                for (int i = 0; i < 256; i++) {
                    if (i >= numSteps) break;
                    if (accum.a >= 0.95) break;

                    float t = tStart + float(i) * stepSize;
                    vec3 pos = rayOrigin + rayDir * t;

                    // Convert to texture coordinates [0, 1]
                    vec3 texCoord = (pos - volumeMin) / (volumeMax - volumeMin);

                    // Sample magnitude first to skip low-intensity regions early
                    float mag = texture(magnitudeTexture, texCoord).r;
                    if (mag < threshold) continue;

                    ${hasVelocity ? `
                    // Animated LIC: offset texture coordinate OPPOSITE to velocity
                    // This makes the pattern appear to flow WITH the velocity
                    vec3 vel = texture(velocityTexture, texCoord).rgb;
                    // Convert from [0,1] back to [-1,1] direction
                    vel = vel * 2.0 - 1.0;
                    // Continuous phase that wraps smoothly
                    float phase = mod(uTime * animationSpeed * 0.3, 1.0) * 0.2;
                    // Subtract velocity to make pattern flow in velocity direction
                    vec3 animatedTexCoord = texCoord - vel * phase;
                    // Wrap coordinates for seamless tiling
                    animatedTexCoord = fract(animatedTexCoord + 1.0);
                    float lic = texture(licTexture, animatedTexCoord).r;
                    ` : `
                    float lic = texture(licTexture, texCoord).r;
                    `}

                    // Get color from magnitude
                    vec3 magColor = texture(colormapTexture, vec2(mag, 0.5)).rgb;

                    // Apply contrast enhancement to LIC pattern
                    float enhancedLIC = clamp((lic - 0.5) * 2.0 + 0.5, 0.0, 1.0);

                    // Modulate color by LIC pattern
                    // When licWeight=1: pure LIC modulation (0.3 to 1.0 range for visibility)
                    // When licWeight=0: no LIC effect
                    float licMod = mix(1.0, 0.3 + 0.7 * enhancedLIC, licWeight);
                    vec3 color = magColor * licMod * brightness;

                    // Sample opacity based on magnitude
                    float sampleOpacity = mag * opacity * 0.15;

                    // Front-to-back compositing
                    vec4 src = vec4(color * sampleOpacity, sampleOpacity);
                    accum += (1.0 - accum.a) * src;
                }

                if (accum.a < 0.01) {
                    discard;
                }

                gl_FragColor = accum;
            }
        `;

        const uniforms = {
            licTexture: { value: this.licTexture },
            magnitudeTexture: { value: this.magnitudeTexture },
            colormapTexture: { value: this.colormapTexture },
            volumeMin: { value: new THREE.Vector3(xMin, yMin, zMin) },
            volumeMax: { value: new THREE.Vector3(xMax, yMax, zMax) },
            opacity: { value: this.opacity },
            threshold: { value: this.threshold },
            numSteps: { value: this.steps },
            brightness: { value: this.brightness },
            licWeight: { value: this.licWeight },
            uTime: { value: 0.0 },
            animationSpeed: { value: this.animationSpeed }
        };

        if (hasVelocity) {
            uniforms.velocityTexture = { value: this.velocityTexture };
        }

        return new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            side: THREE.BackSide,
            transparent: true,
            depthWrite: false
        });
    }

    /**
     * Update camera position for ray marching
     * Note: cameraPosition is automatically updated by Three.js for ShaderMaterial
     * This method is kept for API compatibility but is now a no-op
     */
    updateCamera(camera) {
        // Three.js automatically provides cameraPosition uniform to shaders
    }

    /**
     * Update rendering parameters
     * @param {Object} params - New parameters
     */
    updateParameters(params) {
        if (!this.material) return;

        if (params.opacity !== undefined) {
            this.opacity = params.opacity;
            this.material.uniforms.opacity.value = params.opacity;
        }

        if (params.threshold !== undefined) {
            this.threshold = params.threshold;
            this.material.uniforms.threshold.value = params.threshold;
        }

        if (params.steps !== undefined) {
            this.steps = params.steps;
            this.material.uniforms.numSteps.value = params.steps;
        }

        if (params.brightness !== undefined) {
            this.brightness = params.brightness;
            this.material.uniforms.brightness.value = params.brightness;
        }

        if (params.licWeight !== undefined) {
            this.licWeight = params.licWeight;
            this.material.uniforms.licWeight.value = params.licWeight;
        }

        if (params.colormap !== undefined) {
            this.colormap = params.colormap;
            this._createColormapTexture();
            this.material.uniforms.colormapTexture.value = this.colormapTexture;
        }

        if (params.animationSpeed !== undefined) {
            this.animationSpeed = params.animationSpeed;
            this.material.uniforms.animationSpeed.value = params.animationSpeed;
        }

        if (params.animated !== undefined) {
            this.animated = params.animated;
            if (this.animated) {
                this.startAnimation();
            } else {
                this.stopAnimation();
            }
        }

        this.material.needsUpdate = true;
    }

    /**
     * Update time uniform for animation
     * Call this in your render loop
     * @param {number} deltaTime - Time since last frame in seconds
     */
    updateTime(deltaTime) {
        if (!this.material || !this.animated) return;

        this.time += deltaTime;
        this.material.uniforms.uTime.value = this.time;
    }

    /**
     * Start animation loop (standalone mode)
     * Use this if not integrating with an external render loop
     */
    startAnimation() {
        if (this.animationId !== null) return; // Already running

        this.animated = true;
        this.lastFrameTime = performance.now();

        const animate = () => {
            if (!this.animated || !this.material) {
                this.animationId = null;
                return;
            }

            const now = performance.now();
            const deltaTime = (now - this.lastFrameTime) / 1000; // Convert to seconds
            this.lastFrameTime = now;

            this.updateTime(deltaTime);

            this.animationId = requestAnimationFrame(animate);
        };

        this.animationId = requestAnimationFrame(animate);
        console.log('[VolumeLIC] Animation started');
    }

    /**
     * Stop animation loop
     */
    stopAnimation() {
        this.animated = false;
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        console.log('[VolumeLIC] Animation stopped');
    }

    /**
     * Reset animation time to zero
     */
    resetAnimation() {
        this.time = 0;
        if (this.material) {
            this.material.uniforms.uTime.value = 0;
        }
    }

    /**
     * Check if animation is currently running
     * @returns {boolean}
     */
    isAnimating() {
        return this.animated && this.animationId !== null;
    }

    /**
     * Toggle visibility
     * @param {boolean} visible
     */
    setVisible(visible) {
        if (this.mesh) {
            this.mesh.visible = visible;
        }
    }

    /**
     * Clean up resources
     */
    dispose() {
        // Stop animation first
        this.stopAnimation();

        if (this.mesh) {
            this.scene.remove(this.mesh);
            if (this.mesh.geometry) this.mesh.geometry.dispose();
        }

        if (this.material) {
            this.material.dispose();
        }

        if (this.licTexture) {
            this.licTexture.dispose();
        }

        if (this.magnitudeTexture) {
            this.magnitudeTexture.dispose();
        }

        if (this.velocityTexture) {
            this.velocityTexture.dispose();
        }

        if (this.colormapTexture) {
            this.colormapTexture.dispose();
        }

        this.mesh = null;
        this.material = null;
        this.licTexture = null;
        this.magnitudeTexture = null;
        this.velocityTexture = null;
        this.colormapTexture = null;

        console.log('[VolumeLIC] Disposed');
    }
}

// Export for use in viewer
if (typeof window !== 'undefined') {
    window.VolumeLICRenderer = VolumeLICRenderer;
}
